import { EmployeeHoverCard } from "@/components/employee/EmployeeHoverCard.tsx";
import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import EmptyState from "@/components/empty-state/EmptyState.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { Search } from "@/components/search/Search.tsx";
import { getRoleTitle } from "@/pages/settings/roles/role.types.ts";
import { useEmployeesSearchWithRoles } from "@/queries/employees/use-employees.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import { ApiEmployeeFilter, ApiEmployeeWithRoles } from "@/types/employees/employee.types.ts";
import { RoleType } from "@/types/role.types.ts";
import { Box, Page, PaginationBlock, Table, TableCell, Title } from "@skailer-com/uikit";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce, useEffectOnce } from "react-use";

export default function EmployeesWithRoles() {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState<ApiEmployeeFilter>({
    text: searchTerm,
    page_number: 1,
  });

  useDebounce(
    () => {
      setFilter({
        ...filter,
        page_number: 1,
        text: searchTerm,
      });
    },
    300,
    [searchTerm]
  );

  const { data: items, isLoading, isFetching } = useEmployeesSearchWithRoles(filter);
  const columns = useMemo<ColumnDef<ApiEmployeeWithRoles>[]>(
    () => [
      {
        header: t("core:employee"),
        cell: (row) => (
          <EmployeeHoverCard
            employee={row.row.original.employee}
            position={getEmployeePositionTitle(row.row.original.employee)}
          >
            <EmployeeItem showPosition={false} employee={row.row.original.employee} />
          </EmployeeHoverCard>
        ),
        accessorKey: "title",
        ...ColumnSizes.employee,
      },
      {
        header: t("core:position"),
        cell: (row) => (
          <TableCell variant={"secondary"}>
            {getEmployeePositionTitle(row.row.original.employee)}
          </TableCell>
        ),
        accessorKey: "job_title",
        ...ColumnSizes.status,
      },
      {
        header: t("core:roles"),
        cell: (row) => (
          <TableCell variant={"secondary"}>
            {row.row.original.roles
              .filter((role) => [RoleType.all_employees, RoleType.myself].indexOf(role.type) == -1)
              .map((role) => getRoleTitle(role))
              .join(", ")}
          </TableCell>
        ),
        accessorKey: "roles",
        ...ColumnSizes.status,
      },
    ],
    [t]
  );

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.roles.viewedEmployees);
  });

  return (
    <Page.StickyLayout
      toolbar={
        <>
          <Title header={t("core:check_employee_roles")} size={"md"} paddingTop paddingBottom />
          <Box>
            <Search
              placeholder={t("core:name")}
              value={searchTerm}
              onInput={(e) => {
                setSearchTerm(e.currentTarget.value);
              }}
              type={"text"}
            />
          </Box>
        </>
      }
      footer={
        <PaginationBlock
          metadata={items?.metadata}
          onPageChange={(page) => {
            setFilter({ ...filter, page_number: page });
          }}
        />
      }
    >
      <Table
        stickyHeader={true}
        data={items?.items || []}
        columns={columns}
        showSkeleton={isLoading}
        isFetching={isFetching}
        showNotFoundError={filter.text != "" && items?.metadata.total_items == 0}
        notFoundComponent={<EmptyStateNothingFound />}
        emptyStateComponent={<EmptyState />}
      />
    </Page.StickyLayout>
  );
}
